import { motion } from 'framer-motion';
import { Shapes, CornerDownRight, CircleDot } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { useQRStore, DotsType, CornersSquareType, CornersDotType } from '@/stores/qrStore';
import { dotsStyles, cornersSquareStyles, cornersDotStyles } from '@/lib/design-constants';
import { cn } from '@/lib/utils';

export function ShapeTab() {
  const { currentQR, setDesignSettings } = useQRStore();
  const { designSettings } = currentQR;

  return (
    <div className="space-y-6">
      {/* Dots Style */}
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <Shapes className="h-4 w-4 text-primary" />
          <Label className="text-sm font-medium">Module Style</Label>
        </div>
        <div className="grid grid-cols-4 gap-2">
          {dotsStyles.map((style, index) => {
            const isActive = designSettings.dotsType === style.id;
            const Icon = style.icon;
            
            return (
              <Tooltip key={style.id}>
                <TooltipTrigger asChild>
                  <motion.button
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: index * 0.03 }}
                    onClick={() => setDesignSettings({ dotsType: style.id as DotsType })}
                    className={cn(
                      'flex flex-col items-center gap-2 p-3 rounded-xl border transition-all duration-200',
                      isActive
                        ? 'border-primary bg-primary/10 text-primary'
                        : 'border-border/50 hover:border-primary/50 hover:bg-muted/50'
                    )}
                  >
                    <Icon className="h-6 w-6" />
                    <span className="text-xs font-medium">{style.name}</span>
                  </motion.button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>{style.name} pattern for QR modules</p>
                </TooltipContent>
              </Tooltip>
            );
          })}
        </div>
      </div>

      {/* Corner Squares Style */}
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <CornerDownRight className="h-4 w-4 text-primary" />
          <Label className="text-sm font-medium">Corner Squares</Label>
        </div>
        <div className="grid grid-cols-4 gap-2">
          {cornersSquareStyles.map((style, index) => {
            const isActive = designSettings.cornersSquareType === style.id;
            const Icon = style.icon;
            
            return (
              <motion.button
                key={style.id}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: index * 0.03 }}
                onClick={() => setDesignSettings({ cornersSquareType: style.id as CornersSquareType })}
                className={cn(
                  'flex flex-col items-center gap-2 p-3 rounded-xl border transition-all duration-200',
                  isActive
                    ? 'border-primary bg-primary/10 text-primary'
                    : 'border-border/50 hover:border-primary/50 hover:bg-muted/50'
                )}
              >
                <Icon className="h-5 w-5" />
                <span className="text-xs font-medium">{style.name}</span>
              </motion.button>
            );
          })}
        </div>
      </div>

      {/* Corner Dots Style */}
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <CircleDot className="h-4 w-4 text-primary" />
          <Label className="text-sm font-medium">Corner Dots</Label>
        </div>
        <div className="grid grid-cols-4 gap-2">
          {cornersDotStyles.map((style, index) => {
            const isActive = designSettings.cornersDotType === style.id;
            const Icon = style.icon;
            
            return (
              <motion.button
                key={style.id}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: index * 0.03 }}
                onClick={() => setDesignSettings({ cornersDotType: style.id as CornersDotType })}
                className={cn(
                  'flex flex-col items-center gap-2 p-3 rounded-xl border transition-all duration-200',
                  isActive
                    ? 'border-primary bg-primary/10 text-primary'
                    : 'border-border/50 hover:border-primary/50 hover:bg-muted/50'
                )}
              >
                <Icon className="h-5 w-5" />
                <span className="text-xs font-medium">{style.name}</span>
              </motion.button>
            );
          })}
        </div>
      </div>

      {/* Custom Corner Colors */}
      <div className="p-4 rounded-lg border border-border/50 space-y-4">
        <Label className="text-sm font-medium">Custom Corner Colors</Label>
        
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label className="text-xs text-muted-foreground">Corner Squares</Label>
            <div className="flex gap-2">
              <Input
                type="color"
                value={designSettings.cornersSquareColor}
                onChange={(e) => setDesignSettings({ cornersSquareColor: e.target.value })}
                className="w-10 h-9 p-1 cursor-pointer"
              />
              <Input
                type="text"
                value={designSettings.cornersSquareColor}
                onChange={(e) => setDesignSettings({ cornersSquareColor: e.target.value })}
                className="flex-1 font-mono uppercase text-sm"
                maxLength={7}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-xs text-muted-foreground">Corner Dots</Label>
            <div className="flex gap-2">
              <Input
                type="color"
                value={designSettings.cornersDotColor}
                onChange={(e) => setDesignSettings({ cornersDotColor: e.target.value })}
                className="w-10 h-9 p-1 cursor-pointer"
              />
              <Input
                type="text"
                value={designSettings.cornersDotColor}
                onChange={(e) => setDesignSettings({ cornersDotColor: e.target.value })}
                className="flex-1 font-mono uppercase text-sm"
                maxLength={7}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
